import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Filter, Inbox, FileArchive, Plus, X } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import MobileListCard from "@/components/MobileListCard";

const STATUSES = ["new", "contacted", "quoted", "converted", "closed"];

function StatusBadge({ status }) {
    const cls = {
        new: "badge-new",
        contacted: "badge-contacted",
        quoted: "badge-quoted",
        converted: "badge-converted",
        closed: "badge-closed",
    }[status] || "badge-muted";
    return (
        <span className={`badge-pill ${cls}`} data-testid={`lead-status-badge-${status}`}>
            {status}
        </span>
    );
}

export default function Leads() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const initialStatus = params.get("status") || "";

    const [leads, setLeads] = useState(null);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState(initialStatus);
    const [showCreate, setShowCreate] = useState(false);

    const fetchLeads = async () => {
        setErr("");
        try {
            const { data } = await api.get("/admin/leads", {
                params: {
                    ...(statusFilter ? { status_filter: statusFilter } : {}),
                    ...(q.trim() ? { q: q.trim() } : {}),
                },
            });
            setLeads(data.leads);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        fetchLeads();
        // sync to URL
        const next = new URLSearchParams(params);
        if (statusFilter) next.set("status", statusFilter);
        else next.delete("status");
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const counts = useMemo(() => {
        const c = { all: leads?.length || 0 };
        STATUSES.forEach((s) => (c[s] = 0));
        (leads || []).forEach((l) => (c[l.status] = (c[l.status] || 0) + 1));
        return c;
    }, [leads]);

    return (
        <div className="page-shell">
            <header className="fade-up">
                <p className="eyebrow">Leads</p>
                <div className="flex items-end justify-between gap-4 flex-wrap mt-3">
                    <div>
                        <h1 className="h1" data-testid="leads-title">
                            Incoming leads
                        </h1>
                        <p className="text-muted mt-2 max-w-[640px]">
                            Every submission from{" "}
                            <span style={{ color: "var(--brand-dark)", fontWeight: 600 }}>
                                cprpainting.com/contact
                            </span>{" "}
                            lands here. Click any row to follow up.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setShowCreate(true)}
                        data-testid="add-lead-btn"
                    >
                        <Plus size={14} />
                        Add Lead
                    </button>
                </div>
            </header>

            {/* Filter chips */}
            <div className="mt-6 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    className={`btn ${statusFilter === "" ? "btn-primary" : "btn-ghost"}`}
                    onClick={() => setStatusFilter("")}
                    data-testid="filter-all"
                >
                    All ({counts.all})
                </button>
                {STATUSES.map((s) => (
                    <button
                        key={s}
                        type="button"
                        className={`btn ${statusFilter === s ? "btn-primary" : "btn-ghost"}`}
                        onClick={() => setStatusFilter(s)}
                        data-testid={`filter-${s}`}
                    >
                        <span className="capitalize">{s}</span>
                        <span className="text-xs opacity-70">({counts[s] || 0})</span>
                    </button>
                ))}
            </div>

            {/* Search */}
            <form
                className="mt-4 flex gap-2 flex-wrap"
                onSubmit={(e) => {
                    e.preventDefault();
                    fetchLeads();
                }}
            >
                <div className="relative flex-1 min-w-[240px]">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-muted)]"
                    />
                    <input
                        type="text"
                        placeholder="Search by name, email, or phone"
                        className="field-input"
                        style={{ paddingLeft: "2.25rem" }}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="lead-search-input"
                    />
                </div>
                <button type="submit" className="btn btn-secondary" data-testid="lead-search-submit">
                    <Filter size={14} />
                    Search
                </button>
            </form>

            {err && (
                <div
                    className="brand-card mt-6"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="leads-error"
                >
                    {err}
                </div>
            )}

            <div className="brand-card mt-6 p-0 overflow-hidden fade-up" data-testid="leads-card">
                {leads === null ? (
                    <div className="p-8 text-muted">Loading leads…</div>
                ) : leads.length === 0 ? (
                    <div className="p-10 text-center">
                        <Inbox
                            size={28}
                            style={{ color: "var(--brand-muted)" }}
                            className="mx-auto"
                        />
                        <p className="text-muted mt-3" data-testid="leads-empty">
                            No leads match the current filter yet.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="sm:hidden p-3" data-testid="leads-cards">
                            {leads.map((l) => (
                                <MobileListCard
                                    key={l.id}
                                    to={`/leads/${l.id}`}
                                    testId={`lead-card-${l.id}`}
                                    title={l.name}
                                    subtitle={l.property_address || l.email}
                                    right={<StatusBadge status={l.status} />}
                                    meta={[
                                        { label: "Phone", value: l.phone },
                                        { label: "Service", value: l.service_type },
                                        {
                                            label: "Date",
                                            value: l.created_at
                                                ? new Date(l.created_at).toLocaleDateString()
                                                : null,
                                        },
                                    ]}
                                />
                            ))}
                        </div>
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="brand-table" data-testid="leads-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Contact</th>
                                    <th>Service</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map((l) => (
                                    <tr
                                        key={l.id}
                                        onClick={() => navigate(`/leads/${l.id}`)}
                                        data-testid={`lead-row-${l.id}`}
                                    >
                                        <td>
                                            <p className="font-display font-bold inline-flex items-center gap-1.5">
                                                {l.name}
                                                {l.bid_intake_job_ids?.length > 0 && (
                                                    <span
                                                        title={`${l.bid_intake_job_ids.length} bid document${l.bid_intake_job_ids.length === 1 ? "" : "s"}`}
                                                        className="inline-flex items-center"
                                                        style={{ color: "var(--brand-warning)" }}
                                                        data-testid={`lead-row-bid-icon-${l.id}`}
                                                    >
                                                        <FileArchive size={13} />
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-muted">
                                                {l.property_address || "—"}
                                            </p>
                                        </td>
                                        <td>
                                            <p className="text-sm">{l.email}</p>
                                            <p className="text-xs text-muted">{l.phone}</p>
                                        </td>
                                        <td className="text-sm">{l.service_type}</td>
                                        <td className="text-sm text-muted">
                                            {l.created_at
                                                ? new Date(l.created_at).toLocaleDateString()
                                                : "—"}
                                        </td>
                                        <td>
                                            <StatusBadge status={l.status} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </>
                )}
            </div>

            {/* Create lead modal */}
            {showCreate && (
                <CreateLeadModal
                    onClose={() => setShowCreate(false)}
                    onCreated={() => {
                        setShowCreate(false);
                        fetchLeads();
                    }}
                />
            )}
        </div>
    );
}

function CreateLeadModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        property_address: "",
        service_type: "Interior Painting",
        notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            await api.post("/admin/leads", {
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
                property_address: form.property_address.trim(),
                service_type: form.service_type,
                notes: form.notes.trim(),
            });
            onCreated();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
            style={{ background: "rgba(26,26,26,0.5)" }}
            onClick={onClose}
            data-testid="create-lead-modal"
        >
            <form
                className="brand-card max-w-[480px] w-full"
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
            >
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <p className="eyebrow">Add</p>
                        <h2 className="h2 mt-2">New lead</h2>
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="modal-close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="mt-5 flex flex-col gap-4">
                    <div>
                        <label className="field-label" htmlFor="cl-name">Name *</label>
                        <input
                            id="cl-name"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="field-input"
                            data-testid="cl-name"
                        />
                    </div>
                    <div className="flex gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                            <label className="field-label" htmlFor="cl-phone">Phone *</label>
                            <input
                                id="cl-phone"
                                required
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                className="field-input"
                                data-testid="cl-phone"
                            />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <label className="field-label" htmlFor="cl-email">Email *</label>
                            <input
                                id="cl-email"
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="field-input"
                                data-testid="cl-email"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="field-label" htmlFor="cl-address">Address</label>
                        <input
                            id="cl-address"
                            value={form.property_address}
                            onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                            className="field-input"
                            data-testid="cl-address"
                        />
                    </div>
                    <div>
                        <label className="field-label" htmlFor="cl-service">Service type</label>
                        <select
                            id="cl-service"
                            className="field-select"
                            value={form.service_type}
                            onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                            data-testid="cl-service"
                        >
                            {["Interior Painting", "Exterior Painting", "Cabinet Refinishing", "Deck Staining", "Drywall Repair", "Other"].map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="field-label" htmlFor="cl-notes">Notes</label>
                        <textarea
                            id="cl-notes"
                            rows={3}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="field-input"
                            data-testid="cl-notes"
                        />
                    </div>
                    {err && (
                        <div className="text-sm" style={{ color: "var(--brand-red)" }}>
                            {err}
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="modal-cancel">
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={saving} data-testid="modal-save">
                            {saving ? "Saving…" : "Save lead"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
